import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import find from 'lodash/find';
import union from 'lodash/union';
import take from 'lodash/take';
import ScrollLazyLoad from '@Components/Results/utils/scroll-lazy-load.component';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { resultsConfig } from '@Constants/Results';
import Breadcrumb from '@Services/Breadcrumb';
import Entries from '@Services/Entries';
import ReactionTopButtons from '@Components/Results/reactions/reaction-details/ReactionTopButtons';
import ReactionDetailsTable from '@Components/Results/reactions/reaction-details/ReactionDetailsTable';
import ResultDetailCard from '@Components/Results/common/ResultDetailCard';
import ReactionFindSimilarContent from '@Components/Results/reactions/reaction-details/ReactionFindSimilarContent';
import { withToggleConditionsGlobalConsumer } from '@Pages/Results/ToggleConditionsGlobalHOCs';
import { registerReactionCitationHandler } from '@Containers/Summary/reactionCitationCoordinator';
import { focusAndScrollReferenceTarget } from '@Components/Results/resultListIds';

const MINIMAL_VIEW_MAX_CONDITIONS_COUNT = 3;

function _defaultDetailsLimit(detailsCount, isViewPreview = false, isMinimalView = false) {
  if (isViewPreview) {
    return Math.min(detailsCount, 100);
  }
  return detailsCount <= 5 && !isMinimalView ? detailsCount : MINIMAL_VIEW_MAX_CONDITIONS_COUNT;
}

function getDetailsOpen(metadata, type, reaction) {
  const conditionsState = Breadcrumb.getResultsStateForDb(metadata.databaseId, 'reactionConditions');
  const onResultsPage = !metadata.isViewPreview && !metadata.fromDocuments && !metadata.fromRT;

  // check restoring show/hide reaction conditions only on result page
  const checkRestore = onResultsPage && conditionsState;

  // restore of conditions state for reaction with the same id
  if (checkRestore && conditionsState?.[metadata.id]) {
    return conditionsState?.[metadata.id]?.[type];
    // hide conditions if global toggle was activated and there is no manual state for reaction with this id
  }
  if (checkRestore && !conditionsState[metadata.id] && !conditionsState.show) {
    return false;
  }
  // all other cases for results/preview page or reactions in other contexts
  switch (type) {
    case 'isHitDetailsOpen':
      return !metadata.fromDocuments && !metadata.fromRT;

    case 'isAllDetailsOpen':
      return !metadata.isViewPreview && !metadata.fromDocuments && !metadata.fromRT && isEmpty(reaction?.hitDetails || []);
    default:
      return undefined;
  }
}

function shapeDetails(details, products) {
  if (!isArray(details) || !details.length) return [];
  return details.map((item) => {
    if (isArray(item.yields)) {
      return {
        ...item,
        yields: map(products, (product) => find(item.yields, { id: product.details?.id })),
      };
    }
    return item;
  });
}

function findSummaryConditionRow(conditionId) {
  return Array.from(document.querySelectorAll('[data-summary-condition-id]')).find(
    (element) => element.getAttribute('data-summary-condition-id') === conditionId
  );
}

function waitForSummaryConditionRow(conditionId) {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const findRow = () => {
      const row = findSummaryConditionRow(conditionId);
      if (row) {
        focusAndScrollReferenceTarget(row, { block: 'center' });
        resolve(row);
        return;
      }
      attempts += 1;
      if (attempts >= 40) {
        reject(new Error('Referenced condition row did not become available.'));
        return;
      }
      setTimeout(findRow, 50);
    };
    findRow();
  });
}

function ReactionDetailsComponent(props) {
  const { metadata, reaction, spinnerId, displayAllConditions, showConditions } = props;

  // Details/hitDetails state shape may get mutated in place in showMore/showLess
  const initialDetails = React.useMemo(() => {
    if (isArray(reaction.details) && reaction.details.length) {
      const limit = _defaultDetailsLimit(reaction.detailsCount, metadata.isViewPreview, !displayAllConditions);
      if (reaction.details?.length > limit) {
        reaction.details = reaction.details?.slice(0, limit);
      }
      return shapeDetails(reaction.details, reaction.products);
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reaction.details, reaction.detailsCount, metadata.isViewPreview, displayAllConditions, reaction.products]);

  const initialHitDetails = React.useMemo(() => {
    if (reaction.hitDetails) {
      const hitsLimit = _defaultDetailsLimit(reaction.hitDetailsCount, metadata.isViewPreview, !displayAllConditions);
      const slicedHitDetails = reaction.hitDetails?.length > hitsLimit ? reaction.hitDetails.slice(0, hitsLimit) : reaction.hitDetails;
      return shapeDetails(slicedHitDetails, reaction.products);
    }
    return [];
  }, [reaction.hitDetails, reaction.hitDetailsCount, metadata.isViewPreview, displayAllConditions, reaction.products]);

  const initialConditionsNumber = useRef(initialDetails.length);
  const initialHitsNumber = useRef(initialHitDetails.length);
  const detailsLoadingPromise = useRef(null);
  // Ref to the ReactionTopButtons toggle container, so closing a card can return
  // focus to that view's toggle within this reaction only (no global event).
  const topButtonsRef = useRef(null);

  const [details, setDetails] = useState(initialDetails);
  const [hitDetails, setHitDetails] = useState(initialHitDetails);
  const detailsRef = useRef(initialDetails);
  const hitDetailsRef = useRef(initialHitDetails);
  const [isHitDetailsOpen, setIsHitDetailsOpen] = useState(getDetailsOpen(metadata, 'isHitDetailsOpen', reaction));
  const [isAllDetailsOpen, setIsAllDetailsOpen] = useState(getDetailsOpen(metadata, 'isAllDetailsOpen', reaction));
  const [isFindSimilarOpen, setIsFindSimilarOpen] = useState(false);

  useEffect(() => {
    if (metadata.expandConditions) {
      setIsAllDetailsOpen(true);
    }
  }, [metadata.expandConditions]);

  useEffect(() => {
    const conditionsState = Breadcrumb.getResultsStateForDb(metadata.databaseId, 'reactionConditions') || {};

    conditionsState[metadata.id] = {
      isAllDetailsOpen,
      isHitDetailsOpen,
    };

    if (!metadata.isViewPreview && !metadata.fromDocuments && !metadata.fromRT) {
      Breadcrumb.updateResultsStateForDb(metadata.databaseId, 'reactionConditions', conditionsState);
    }
    // handle toggling when showConditions changes
  }, [isAllDetailsOpen, isHitDetailsOpen, metadata]);

  useEffect(() => {
    // Only run on showConditions change specifically
    if (showConditions !== undefined) {
      setIsHitDetailsOpen(showConditions);
      setIsAllDetailsOpen(showConditions ? isEmpty(reaction.hitDetails) : false);
      setIsFindSimilarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConditions]);

  const toggleHitDetailsOpen = useCallback(() => {
    setIsHitDetailsOpen((prev) => {
      const next = !prev;
      // Exclusive: opening Hit details closes the Conditions view.
      if (next) {
        setIsAllDetailsOpen(false);
        setIsFindSimilarOpen(false);
      }
      return next;
    });
  }, []);

  const showMoreReactionDetails = useCallback(
    (hitData, firstItem = 1, lastItem = 100) => {
      if (detailsLoadingPromise.current) {
        return detailsLoadingPromise.current;
      }

      const { resultName, order, sort } = metadata;
      const { index } = reaction;
      const databaseId = reaction?.databaseId || metadata?.databaseId;

      const request = Entries.getReactionDetails({ index, resultName, databaseId, firstItem, lastItem, order, sort, hitData })
        .then((data) => {
          if (isArray(data)) {
            if (hitData) {
              const nextHitDetails = union(hitDetailsRef.current, data);
              hitDetailsRef.current = nextHitDetails;
              setHitDetails(nextHitDetails);
            } else {
              const shaped = shapeDetails(data, reaction.products);
              const nextDetails = union(detailsRef.current, shaped);
              detailsRef.current = nextDetails;
              setDetails(nextDetails);
            }
          }
          return data;
        })
        .catch((e) => {
          if (window.Logger && window.Logger.reject) {
            window.Logger.reject(e);
          }
          return [];
        })
        .finally(() => {
          if (detailsLoadingPromise.current === request) {
            detailsLoadingPromise.current = null;
          }
        });

      detailsLoadingPromise.current = request;
      return request;
    },

    [metadata, reaction]
  );

  const loadInitialReactionDetails = useCallback(() => {
    let limit = Math.min(metadata.detailsCount, resultsConfig.INITIAL_ITEMS_AMOUNT.reactionDetails);

    if (isEmpty(details)) {
      // case lazyload
      if (reaction.hitDetailsCount === 0) {
        limit = _defaultDetailsLimit(reaction.detailsCount, metadata.isViewPreview, !displayAllConditions);
        initialConditionsNumber.current = limit;
      }

      return showMoreReactionDetails(false, 1, limit);
    }
    if (details.length <= limit) {
      // case no lazyload, retrieved as combinedResponse
      initialConditionsNumber.current = 0;
      initialHitsNumber.current = 0;
      return showMoreReactionDetails(false, details.length + 1, limit);
    }

    return Promise.resolve(details);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, metadata.detailsCount, metadata.isViewPreview, displayAllConditions, reaction.hitDetailsCount]);

  useEffect(() => {
    if (isAllDetailsOpen && reaction.detailsCount > 0 && isEmpty(details) && !detailsLoadingPromise.current) {
      loadInitialReactionDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllDetailsOpen, details, reaction.detailsCount]);

  useEffect(() => {
    if (metadata.fromRT || !metadata.resultName || !reaction.id) {
      return undefined;
    }

    return registerReactionCitationHandler({ resultName: metadata.resultName, reactionId: reaction.id }, async ({ conditionId }) => {
      const prefix = `${reaction.id}-`;
      const rowNumber = conditionId?.startsWith(prefix) ? Number(conditionId.slice(prefix.length)) : NaN;
      if (!Number.isInteger(rowNumber) || rowNumber < 1) {
        throw new Error('Invalid reaction condition reference.');
      }

      const hasHitDetails = reaction.hitDetailsCount > 0;
      if (hasHitDetails) {
        setIsAllDetailsOpen(false);
        setIsHitDetailsOpen(true);
      } else {
        setIsHitDetailsOpen(false);
        setIsAllDetailsOpen(true);
      }

      const targetRowsRef = hasHitDetails ? hitDetailsRef : detailsRef;
      if (targetRowsRef.current.length < rowNumber) {
        await showMoreReactionDetails(hasHitDetails, targetRowsRef.current.length + 1, rowNumber);
      }
      if (targetRowsRef.current.length < rowNumber) {
        await showMoreReactionDetails(hasHitDetails, targetRowsRef.current.length + 1, rowNumber);
      }

      return waitForSummaryConditionRow(conditionId);
    });
  }, [metadata.fromRT, metadata.resultName, reaction.id, reaction.hitDetailsCount, showMoreReactionDetails]);

  const toggleAllDetailsOpen = useCallback(() => {
    if (isEmpty(details) && reaction.detailsCount > 0) {
      setIsHitDetailsOpen(false); // Exclusive: opening Conditions closes Hit details.
      setIsFindSimilarOpen(false);
      setIsAllDetailsOpen(true);
      loadInitialReactionDetails();
      return;
    }

    setIsAllDetailsOpen((prev) => {
      const next = !prev;
      // Exclusive: opening Conditions closes the Hit details view.
      if (next) {
        setIsHitDetailsOpen(false);
        setIsFindSimilarOpen(false);
      }
      return next;
    });
  }, [details, reaction.detailsCount, loadInitialReactionDetails]);

  const toggleFindSimilarOpen = useCallback(() => {
    setIsFindSimilarOpen((prev) => {
      const next = !prev;
      if (next) {
        setIsHitDetailsOpen(false);
        setIsAllDetailsOpen(false);
      }
      return next;
    });
  }, []);

  const focusToggle = useCallback((toggleE2e) => {
    topButtonsRef.current?.querySelector(`[data-e2e="${toggleE2e}"]`)?.focus();
  }, []);

  const closeHitDetails = useCallback(() => {
    setIsHitDetailsOpen(false);
    focusToggle('reaction-hits');
  }, [focusToggle]);

  const closeAllDetails = useCallback(() => {
    setIsAllDetailsOpen(false);
    focusToggle('reaction-conditions');
  }, [focusToggle]);

  const showLessReactionDetails = useCallback((hitData) => {
    if (hitData) {
      setHitDetails((prev) => {
        const next = take(prev, initialHitsNumber.current);
        hitDetailsRef.current = next;
        return next;
      });
    } else {
      setDetails((prev) => {
        const firstN = take(prev, initialConditionsNumber.current);
        detailsRef.current = firstN;
        if (isEmpty(firstN)) {
          setIsAllDetailsOpen(false);
        }
        return firstN;
      });
    }
  }, []);

  const getHitDetailsTable = () =>
    !isEmpty(hitDetails) ? (
      <ReactionDetailsTable
        metadata={metadata}
        reactionId={reaction.id}
        databaseId={reaction.databaseId}
        details={hitDetails}
        detailsCount={reaction.hitDetailsCount}
        largestProduct={reaction.largestProduct}
        initialDetailsNumber={initialHitsNumber.current}
        isHitDetails
        hasHitDetails
        spinnerId={spinnerId}
        index={reaction.index}
        showMoreHandler={({ firstItem, lastItem }) => showMoreReactionDetails(true, firstItem, lastItem)}
        showLessHandler={hitDetails.length > initialHitsNumber.current ? () => showLessReactionDetails(true) : null}
        showNextConditions={
          reaction.hitDetailsCount !== reaction.detailsCount &&
          !isAllDetailsOpen &&
          reaction.detailsCount > 0 &&
          `Show ${reaction.detailsCount > 100 ? 'next 100' : 'all'} conditions`
        }
        toggleAllDetails={toggleAllDetailsOpen}
        displayFooter={displayAllConditions}
      />
    ) : null;

  const getDetailsTable = () =>
    !isEmpty(details) ? (
      <ReactionDetailsTable
        metadata={metadata}
        reactionId={reaction.id}
        databaseId={reaction.databaseId}
        details={details}
        detailsCount={reaction.detailsCount}
        largestProduct={reaction.largestProduct}
        initialDetailsNumber={initialConditionsNumber.current}
        isHitDetails={false}
        hasHitDetails={reaction.hitDetailsCount > 0}
        spinnerId={spinnerId}
        index={reaction.index}
        showMoreHandler={({ firstItem, lastItem }) => showMoreReactionDetails(false, firstItem, lastItem)}
        showLessHandler={details.length > initialConditionsNumber.current ? () => showLessReactionDetails(false) : null}
        showNextConditions={false}
        displayFooter={displayAllConditions}
      />
    ) : (
      <ScrollLazyLoad load={loadInitialReactionDetails} viewportOffset={1} container=".results-wrapper" />
    );

  // In the full-conditions view the opened table is wrapped in a titled Leyden card
  // with a close control. In the minimal view (displayAllConditions === false) and in
  // any Retrosynthesis surface (metadata.fromRT) the content renders as before, no card.
  const renderConditionsCard = ({ id, title, closeDataE2e, onClose, content }) => {
    if (!content) return null;
    if (!displayAllConditions || metadata.fromRT) return content;
    return (
      <ResultDetailCard id={id} title={title} closeDataE2e={closeDataE2e} onClose={onClose} hideHeading>
        {content}
      </ResultDetailCard>
    );
  };

  return (
    <div className="react-reaction-details-wrapper" data-summary-reaction-details-id={!metadata.fromRT ? reaction.id : undefined}>
      {displayAllConditions && (
        <ReactionTopButtons
          metadata={metadata}
          reaction={reaction}
          index={reaction.index}
          isHitDetailsOpen={isHitDetailsOpen}
          isAllDetailsOpen={isAllDetailsOpen}
          toggleHitDetailsOpen={toggleHitDetailsOpen}
          toggleAllDetailsOpen={toggleAllDetailsOpen}
          isFindSimilarOpen={isFindSimilarOpen}
          toggleFindSimilarOpen={toggleFindSimilarOpen}
          topButtonsRef={topButtonsRef}
        />
      )}
      {reaction.hitDetailsCount > 0 &&
        isHitDetailsOpen &&
        renderConditionsCard({
          id: `reaction-hit-details-${reaction.index}`,
          title: 'Hit details',
          closeDataE2e: 'e2e-hit-details-close',
          onClose: closeHitDetails,
          content: getHitDetailsTable(),
        })}
      {reaction.detailsCount > 0 &&
        isAllDetailsOpen &&
        renderConditionsCard({
          id: `reaction-details-${reaction.index}`,
          title: 'Conditions',
          closeDataE2e: 'e2e-conditions-close',
          onClose: closeAllDetails,
          content: getDetailsTable(),
        })}
      {isFindSimilarOpen && (
        <ResultDetailCard id={`reaction-find-similar-${reaction.index}`} hideHeading>
          <ReactionFindSimilarContent metadata={metadata} reaction={reaction} databaseId={reaction?.databaseId || metadata?.databaseId} />
        </ResultDetailCard>
      )}
    </div>
  );
}

ReactionDetailsComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  reaction: PropTypes.object.isRequired,
  spinnerId: PropTypes.string.isRequired,
  databaseId: PropTypes.string,
  displayAllConditions: PropTypes.bool,
  showConditions: PropTypes.bool,
};

export default withToggleConditionsGlobalConsumer()(ReactionDetailsComponent);
